import {generateMedicalFacilitySEO} from '@helper/seo/generators/medical-facility';
import {generateShareholderSEO} from '@helper/seo/generators/shareholder';
import {generateSpecialtySEO} from '@helper/seo/generators/specialty';
import {generateNewsSEO} from '@helper/seo/generators/news';
import {generateDoctorSEO} from '@helper/seo/generators/doctor';
import {changeToSlug, getSlugType} from '@helper/string.helper';
import NewsService from '@services/news.service';
import UserService from '@services/user.service';
import MedicalFacility from '@services/medical-facility.service';
import DepartmentService from '@services/department.service';
import StaticContentService from '@services/static-content.service';
import {getUuidFromSlug} from '@helper/string.helper';
import SpecialistDoctorDetailPage from 'pages/chuyen-gia-bac-si-chi-tiet';
import NewsDetailsPages from 'pages/tin-tuc-chi-tiet';
import UuDaiDetailsPages from 'pages/uu-dai-chi-tiet';
import BaoChiNoiVeTNHDetailsPages from 'pages/bao-chi-noi-ve-tnh-chi-tiet';
import MedicalFacilityDetailPage from 'pages/co-so-y-te-chi-tiet';
import SpecialtyDetailsHome from 'pages/chuyen-khoa-chi-tiet';
import StaticPage from '@components/common/StaticPage';
import JobDetailPage from 'pages/tuyen-dung-chi-tiet';
import ShareholderDetail from 'pages/quan-he-co-dong-chi-tiet';
import {generateRecruitmentSEO} from '@helper/seo/generators/recruitment';

// Helper function để chuyển null thành chuỗi rỗng
const convertNullToEmptyString = obj => {
    if (obj === null) return '';
    if (obj === undefined) return obj;
    if (typeof obj !== 'object') return obj;

    if (Array.isArray(obj)) {
        return obj.map(item => convertNullToEmptyString(item));
    }

    const newObj = {};
    for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
            newObj[key] = convertNullToEmptyString(obj[key]);
        }
    }
    return newObj;
};

function DyamicPage(props) {
    // Xử lý props, chuyển null thành chuỗi rỗng
    const cleanProps = convertNullToEmptyString(props);

    const renderPageBySlug = () => {
        switch (cleanProps.typeSlug) {
            case 'd':
                if (cleanProps.isPromotion) {
                    return <UuDaiDetailsPages {...cleanProps} />;
                }
                if (cleanProps.isBaoChi) {
                    return <BaoChiNoiVeTNHDetailsPages {...cleanProps} />;
                }
                return <NewsDetailsPages {...cleanProps} />;
            case 'u':
                return <SpecialistDoctorDetailPage {...cleanProps} />;
            case 'm':
                return <MedicalFacilityDetailPage {...cleanProps} />;
            case 's':
                return <SpecialtyDetailsHome {...cleanProps} />;
            case 'r':
                return <JobDetailPage {...cleanProps} />;
            case 'l':
                return <ShareholderDetail {...cleanProps} />;
            case 't':
                return <StaticPage content={cleanProps.staticContent} />;
            default:
                return null;
        }
    };
    return <>{renderPageBySlug()}</>;
}

export async function getServerSideProps(context) {
    const {slug} = context.query || {};
    const {locale} = context;
    // Sử dụng URL production, ưu tiên NEXT_PUBLIC_WEB_URL
    let web_url = process.env.NEXT_PUBLIC_WEB_URL || process.env.WEB_URL || 'https://tnh.com.vn';
    try {
        // Prefer UUID-based doctor detail first
        const uuid = getUuidFromSlug(slug);
        let id, typeSlug;
        if (uuid) {
            id = uuid;
            typeSlug = 'u';
        } else {
            ({id, typeSlug} = getSlugType(slug));
        }

        if (!id) {
            return {
                notFound: true,
            };
        }

        if (typeSlug !== 'u' && isNaN(id)) {
            return {
                notFound: true,
            };
        } else if (typeSlug === 'd') {
            const _newsService = new NewsService();
            try {
                const newsDetail = await _newsService.getNewsById(id, {locale});
                const isPromotion = newsDetail?.news_category_name === 'Ưu đãi';
                const isBaoChi = newsDetail?.news_category_name === 'Báo chí nói về TNH';

                // Tạo SEO cho trang chi tiết tin tức
                const seoPage = generateNewsSEO(newsDetail, slug, web_url);

                return {
                    props: {
                        typeSlug: 'd',
                        preloadedNews: newsDetail || null,
                        isPromotion: isPromotion,
                        isBaoChi: isBaoChi,
                        seoPage,
                    },
                };
            } catch (e) {
                console.error('Error fetching news detail:', e);
                return {notFound: true};
            }
        } else if (typeSlug === 'u') {
            // Doctor detail by UUID in slug
            const uuid = getUuidFromSlug(slug);
            if (!uuid) {
                return {notFound: true};
            }
            const _userService = new UserService(context);
            try {
                const doctor = await _userService.getUserDetail(uuid, {locale});

                // Tạo SEO cho trang chi tiết bác sĩ
                const seoPage = generateDoctorSEO(doctor, slug, web_url, locale);

                return {
                    props: {
                        typeSlug: 'u',
                        preloadedDoctor: doctor || null,
                        seoPage,
                    },
                };
            } catch (e) {
                return {notFound: true};
            }
        } else if (typeSlug === 'm') {
            const _medicalFacilityService = new MedicalFacility(context);
            try {
                const medicalFacilityDetail = await _medicalFacilityService.getMedicalFacilityById(id, {locale});
                const hospitals = await _medicalFacilityService.getMedicalFacilityHome({locale});

                // Xử lý response structure - có thể là object trực tiếp hoặc wrap trong field
                let facility = null;
                if (medicalFacilityDetail) {
                    // Xử lý nếu response là array (trường hợp API trả về array trực tiếp)
                    if (Array.isArray(medicalFacilityDetail)) {
                        facility = medicalFacilityDetail.length > 0 ? medicalFacilityDetail[0] : null;
                    } else if (medicalFacilityDetail.facility) {
                        // Nếu có field facility
                        facility = Array.isArray(medicalFacilityDetail.facility)
                            ? medicalFacilityDetail.facility.length > 0
                                ? medicalFacilityDetail.facility[0]
                                : null
                            : medicalFacilityDetail.facility;
                    } else if (medicalFacilityDetail.data) {
                        // Nếu có field data
                        if (Array.isArray(medicalFacilityDetail.data)) {
                            facility = medicalFacilityDetail.data.length > 0 ? medicalFacilityDetail.data[0] : null;
                        } else if (medicalFacilityDetail.data.facility) {
                            facility = Array.isArray(medicalFacilityDetail.data.facility)
                                ? medicalFacilityDetail.data.facility.length > 0
                                    ? medicalFacilityDetail.data.facility[0]
                                    : null
                                : medicalFacilityDetail.data.facility;
                        } else {
                            facility = medicalFacilityDetail.data;
                        }
                    } else {
                        // Trường hợp response là object trực tiếp
                        facility = medicalFacilityDetail;
                    }
                }

                if (!facility || typeof facility !== 'object' || Array.isArray(facility)) {
                    console.error(
                        '[MedicalFacility SSR] Medical facility not found for id:',
                        id,
                        'Response:',
                        medicalFacilityDetail,
                    );
                    return {notFound: true};
                }

                // Xử lý hospitals response
                let hospitalsList = [];
                if (hospitals) {
                    if (Array.isArray(hospitals)) {
                        hospitalsList = hospitals;
                    } else if (hospitals.data && Array.isArray(hospitals.data)) {
                        hospitalsList = hospitals.data;
                    } else if (hospitals.items && Array.isArray(hospitals.items)) {
                        hospitalsList = hospitals.items;
                    } else if (typeof hospitals === 'object' && !Array.isArray(hospitals)) {
                        // Nếu hospitals là object nhưng không có data/items, có thể là object với keys
                        hospitalsList = Object.values(hospitals).filter(item => item && typeof item === 'object');
                    }
                }

                // Tạo SEO cho trang chi tiết cơ sở y tế
                const seoPage = generateMedicalFacilitySEO(facility, slug, web_url);

                return {
                    props: {
                        typeSlug: 'm',
                        preloadedMedicalFacility: facility,
                        hospitals: hospitalsList,
                        seoPage,
                    },
                };
            } catch (e) {
                console.error('Error fetching medical facility detail:', e);
                return {notFound: true};
            }
        } else if (typeSlug === 's') {
            // Specialty detail
            const _departmentService = new DepartmentService(context);
            const _medicalFacilityService = new MedicalFacility(context);
            try {
                const departmentResponse = await _departmentService.getDepartmentDetail({
                    departmentId: id,
                    locale: locale || 'vn',
                });

                const hospitals = await _medicalFacilityService.getMedicalFacilityHome({
                    locale: locale || 'vn',
                });

                // Xử lý response department
                let departmentData = null;
                if (Array.isArray(departmentResponse) && departmentResponse.length > 0) {
                    departmentData = departmentResponse[0];
                } else if (
                    departmentResponse &&
                    departmentResponse.data &&
                    Array.isArray(departmentResponse.data) &&
                    departmentResponse.data.length > 0
                ) {
                    departmentData = departmentResponse.data[0];
                }

                if (!departmentData) {
                    return {notFound: true};
                }

                // Tạo SEO cho trang chi tiết chuyên khoa
                const seoPage = generateSpecialtySEO(departmentData, slug, web_url);

                return {
                    props: {
                        typeSlug: 's',
                        preloadedDepartment: departmentData,
                        hospitals: hospitals || [],
                        seoPage,
                    },
                };
            } catch (e) {
                console.error('Error fetching department detail:', e);
                return {notFound: true};
            }
        } else if (typeSlug === 'r') {
            // Recruitment detail
            const RecruitmentService = require('@services/recruitment.service').default;
            const _recruitmentService = new RecruitmentService(context);
            try {
                const res = await _recruitmentService.getRecruitmentById(id, {locale});

                // Xử lý response structure - có thể wrap trong data field
                let recruitment = null;
                let relatedRecruitments = [];

                if (res) {
                    if (res.recruitment) {
                        recruitment = res.recruitment;
                        relatedRecruitments = res.relatedRecruitments || [];
                    } else if (res.data && res.data.recruitment) {
                        recruitment = res.data.recruitment;
                        relatedRecruitments = res.data.relatedRecruitments || [];
                    } else if (res.data && !res.data.recruitment) {
                        // Nếu data là object trực tiếp
                        recruitment = res.data;
                        relatedRecruitments = res.data.relatedRecruitments || res.relatedRecruitments || [];
                    } else if (Array.isArray(res) && res.length > 0) {
                        recruitment = res[0];
                    } else {
                        // Response có thể trả về trực tiếp object recruitment
                        recruitment = res;
                    }
                }

                if (!recruitment) {
                    console.error('[Recruitment SSR] Recruitment not found for id:', id, 'Response:', res);
                    return {notFound: true};
                }

                // Tạo SEO cho trang chi tiết tuyển dụng
                const seoPage = generateRecruitmentSEO(recruitment, slug, web_url);

                return {
                    props: {
                        typeSlug: 'r',
                        recruitment,
                        relatedRecruitments: Array.isArray(relatedRecruitments) ? relatedRecruitments : [],
                        seoPage,
                    },
                };
            } catch (e) {
                console.error('Error fetching recruitment detail:', e);
                return {notFound: true};
            }
        }
        if (typeSlug === 'l') {
            // Shareholder detail
            const ShareholderNewsService = require('@services/shareholder-relationship-news.service').default;
            const ShareholderCategoryService = require('@services/shareholder-relationship-category.service').default;
            const AppConfig = require('@services/app-config.service').default;
            const _appConfigService = new AppConfig(context);
            const _shareholderCategoryService = new ShareholderCategoryService(context);
            const _shareholderNewsService = new ShareholderNewsService(context);

            try {
                const contact_info = await _appConfigService.getAppConfig({page: 'HOME', locale: locale});
                const categories = await _shareholderCategoryService
                    .getShareholerRelationshipCategoryOptions({
                        locale: locale || 'vn',
                    })
                    .then(res => {
                        console.log('[Shareholder] categoryRes received', res && (res.data ?? res));
                        return res;
                    })
                    .catch(e => {
                        console.error('[Shareholder] Category error:', e);
                        return null;
                    });
                const news = await _shareholderNewsService.getNewsByCategory({
                    locale: locale,
                    page: 1,
                    page_size: 25,
                    id: id,
                });

                // Xử lý response structure - có thể wrap trong data field
                let shareholderContactFinal = [];
                let shareholderNews = [];
                let categoriesFinal = [];

                if (contact_info) {
                    if (contact_info && typeof contact_info === 'object' && !Array.isArray(contact_info)) {
                        shareholderContactFinal = contact_info;
                    }
                }

                if (Array.isArray(categories) && categories.length > 0) {
                    categoriesFinal = [...categories].sort((a, b) => a.category_id - b.category_id);
                }

                if (news) {
                    if (Array.isArray(news) && news.length > 0) {
                        shareholderNews = news;
                    }
                }

                if (!shareholderNews) {
                    console.error('[Recruitment SSR] Shareholder news not found for id:', id, 'Response:', news);
                    return {notFound: true};
                }

                // Tạo SEO cho trang chi tiết tuyển dụng

                const seoPage = generateShareholderSEO(
                    shareholderNews,
                    categoriesFinal,
                    shareholderContactFinal,
                    slug,
                    web_url,
                );
                return {
                    props: {
                        typeSlug: 'l',
                        categories: categoriesFinal,
                        shareholderContact: shareholderContactFinal,
                        shareholderNews: Array.isArray(shareholderNews) ? shareholderNews : [],
                        seoPage,
                        id,
                    },
                };
            } catch (e) {
                console.error('Error fetching recruitment detail:', e);
                return {notFound: true};
            }
        } else if (typeSlug === 't') {
            // Static Content detail
            const _staticContentService = new StaticContentService(context);
            try {
                const staticContent = await _staticContentService.getStaticContentById(id, {locale});

                if (!staticContent) {
                    return {notFound: true};
                }

                return {
                    props: {
                        typeSlug: 't',
                        staticContent: staticContent || null,
                    },
                };
            } catch (e) {
                console.error('Error fetching static content:', e);
                return {notFound: true};
            }
        }
        return {
            notFound: true,
        };
    } catch (error) {
        // console.log({ error });
        return {
            notFound: true,
        };
    }
}

export default DyamicPage;
